import { Stripe_SubscriptionStatus } from "../graphql/generated/user-hooks";
import { TFunction } from "i18next";

type BadgeInfo = {
  colorScheme: string;
  label: string;
};

/**
 * Resolves the subscription badge following MySensei's priority logic:
 * 1. Past due → red
 * 2. Package → blue
 * 3. Renewal canceled (has cancelAt) → amber with "Cancels on {date}" text
 * 4. Default → map from status
 */
export const getSubscriptionBadge = (args: {
  t: TFunction;
  status: Stripe_SubscriptionStatus;
  cancelAt?: Date | string | null;
  isPackage: boolean;
  formattedCancelDate?: string;
}): BadgeInfo => {
  const { t, status, cancelAt, isPackage, formattedCancelDate } = args;

  // Priority 1: Past due
  if (status === Stripe_SubscriptionStatus.PastDue) {
    return {
      colorScheme: "red",
      label: mapSubscriptionStatusToText({ t, status }),
    };
  }

  // Priority 2: Package
  if (isPackage) {
    return {
      colorScheme: "blue",
      label: mapSubscriptionStatusToText({ t, status }),
    };
  }

  // Priority 3: Renewal canceled (active but has cancelAt)
  if (cancelAt) {
    return {
      colorScheme: "orange",
      label: t("subscriptions.cancelsOn", { date: formattedCancelDate, interpolation: { escapeValue: false } }),
    };
  }

  // Priority 4: Default status mapping
  return {
    colorScheme: mapSubscriptionStatusToBadgeColor(status),
    label: mapSubscriptionStatusToText({ t, status }),
  };
};

export const mapSubscriptionStatusToBadgeColor = (
  status: Stripe_SubscriptionStatus
) => {
  switch (status) {
    case Stripe_SubscriptionStatus.Incomplete:
      return "orange";
    case Stripe_SubscriptionStatus.Trialing:
      return "purple";
    case Stripe_SubscriptionStatus.Active:
      return "green";
    case Stripe_SubscriptionStatus.PastDue:
      return "red";
    case Stripe_SubscriptionStatus.Unpaid:
      return "red";
    default:
      return "gray";
  }
};

export const mapSubscriptionStatusToText = (args: {
  t: TFunction;
  status: Stripe_SubscriptionStatus;
}) => {
  const { t, status } = args;
  switch (status) {
    case Stripe_SubscriptionStatus.Incomplete:
      return t("subscriptions.status.incomplete");
    case Stripe_SubscriptionStatus.IncompleteExpired:
      return t("subscriptions.status.incompleteExpired");
    case Stripe_SubscriptionStatus.Trialing:
      return t("subscriptions.status.trialing");
    case Stripe_SubscriptionStatus.Active:
      return t("subscriptions.status.active");
    case Stripe_SubscriptionStatus.PastDue:
      return t("subscriptions.status.pastDue");
    case Stripe_SubscriptionStatus.Canceled:
      return t("subscriptions.status.canceled");
    case Stripe_SubscriptionStatus.Unpaid:
      return t("subscriptions.status.unpaid");
    case Stripe_SubscriptionStatus.Paused:
      return t("subscriptions.status.paused");
    default:
      return "-";
  }
};
